import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

import AgGridTableContainer from '../../table/ag-grid-table-container';
import Button from 'fuse-ui/components/Button';
import DomainManagementTableActionsRow from '../domain-management/domain-management-table-actions-row';
import Divider from 'fuse-ui/components/Divider';
import opacity from '../../common/theme/colors/opacity';

import { clearFiltersAndSorting } from '../../table/filter-utils';
import { activeClass } from '../../table/table-utils';

import {
    backToTopHandler,
    serverFilterHandler,
    serverPaginationHandler,
    serverSortHandler,
} from '../../table/table-utils';

import { TABLE_TYPES, CUSTOM_FILTER_EXTRA_WIDTH } from '../../table/constants/table-options';
import { DOMAIN_HIERARCHY_PARENT, DOMAIN_MANAGEMENT_TABLE_TYPE_DISCOVERED } from '../../common/constants/definitions';

const styles = ( theme ) => {
    return {
        noMargin: {
            margin: `0 -${theme.spacing.unit * 3}px`
        },
        tableWrapper: {
            border: `1px solid ${opacity.black24}`,
            borderRadius: 2,
            height: 800,
            padding: `0 ${theme.spacing.unit * 3}px`
        },
        viewButton: {
            '&:focus': {
                backgroundColor: 'transparent'
            },
            '&:hover': {
                backgroundColor: 'transparent'
            },
            minWidth: 'auto',
            paddingLeft: 0,
        }
    };
};

export class DomainManagementTable extends Component{
    state = {
        hasScrolled: false,
        noVisibleRows: false,
        scrollToTop: false,
        selectedRowCount: 0,
        selectedRows: [],

    }

    handleRowClick = ( row ) => {
        this.props.onRowClick( row.domainName );
    };

    handleFilterChange = ( params, noVisibleRows ) => {
        const { handleFilterChange, selectedDomainFilter, type } = this.props;

        this.setState( { noVisibleRows } );


        handleFilterChange( type, selectedDomainFilter, params );
    }

    handleSortChange = ( params ) => {
        const { handleSortChange, selectedDomainFilter, type } = this.props;

        handleSortChange( type, selectedDomainFilter,  params );
    }

    handlePaginationChange = () => {
        const { handlePaginationChange, selectedDomainFilter, type } = this.props;

        handlePaginationChange( type, selectedDomainFilter, selectedDomainFilter );
    }

    handleOrgDomainMultiSelectFilter = ( data ) => {
        const { handleOrgDomainMultiSelectFilter, selectedDomainFilter , type } = this.props;

        handleOrgDomainMultiSelectFilter( type, selectedDomainFilter, { orgDomain: data } );
    };

    setHasScrolled = ( hasScrolled ) => this.setState( { hasScrolled } )

    setSelectedRowCount = ( selectedRowCount ) => this.setState( { selectedRowCount } )

    setSelectedRows = ( selectedRows ) => this.setState( { selectedRows } )

    setScrollToTop = ( scrollToTop ) => this.setState( { scrollToTop } )

    viewButtonComponent = ( { data } ) => {
        const { classes, handleViewButtonAction } = this.props;

        return (
            <Button
                className={classes.viewButton}
                onClick={() => handleViewButtonAction( data )}
                variant="text"
            >
                View
            </Button>
        );
    }

    buildCustomGridOptions = () => {
        let customOptions = {
            columnTypes: {
                viewButtonColumn: {
                    cellRendererFramework: this.viewButtonComponent
                }
            },
            defaultColDef: {
                // disable default comparator so it doesn't execute in addition to our serverSortHandler
                comparator: () => {},
            },
            /* deltaRowDataMode preserves selected rows even when data is updated via infinite scrolling.
             * getRowNodeId is required for deltaMode to work properly
             */
            deltaRowDataMode: true,
            getRowNodeId: ( row ) => row.domainName,
            onBodyScroll: ( { api, top } ) => {
                backToTopHandler( top, this.setHasScrolled, this.setScrollToTop );
                serverPaginationHandler( api, this.handlePaginationChange );

                // don't show loading overlay when new data is fetched
                api.hideOverlay();
            },
            onFilterChanged: ( { api } ) => serverFilterHandler( api, this.handleFilterChange ),
            onGridColumnsChanged: ( { api } ) => {
                clearFiltersAndSorting( api );
                this.setSelectedRowCount( api.getSelectedRows().length );
            },
            onSelectionChanged: ( { api } ) => {
                this.setSelectedRows( api.getSelectedRows() );
                this.setSelectedRowCount( api.getSelectedRows().length );
            },
            onSortChanged: ( { api } ) => serverSortHandler( api, this.handleSortChange ),
            postProcessPopup: function( params ){
                if( params.type !== 'columnMenu' ){
                    return;
                }

                const ePopup = params.ePopup;
                /**
                 * Calculate if the Popup filter overflows Aggrid Table
                 * Needs to be wrapped in a setTimout to make this Javascript execute (aka event loop tick)
                 * something is styling the filter after with the width
                 */
                setTimeout( () => {
                    if( ePopup.offsetWidth + ePopup.offsetLeft > ePopup.parentNode.offsetWidth ){
                        const rightSidePositionOfFilter = ePopup.offsetWidth +
                            ePopup.offsetLeft +
                            CUSTOM_FILTER_EXTRA_WIDTH;
                        const offset = rightSidePositionOfFilter - ePopup.parentNode.offsetWidth;
                        const currentLeft = ePopup.style.left.substring( 0, ePopup.style.left.indexOf( 'px' ) );
                        const newLeft = Number( currentLeft - offset );
                        ePopup.style.left = newLeft + 'px';
                    }
                } );

            },
            rowSelection: 'multiple',
            suppressNoRowsOverlay: true,
            suppressRowClickSelection: true,
            suppressScrollOnNewData: true,
        };

        if( this.props.type === DOMAIN_MANAGEMENT_TABLE_TYPE_DISCOVERED ){
            customOptions = {
                ...customOptions,
                rowClass: 'disable-focus',
                suppressRowHoverHighlight: true
            };
        }

        return customOptions;
    }

    render(){

        const { classes, columnDefs, domainGroups, data, isLoading, orgDomainSelectData,
            isActiveTable, selectedDomainFilter, totalItemCount, type  } = this.props;
        const { hasScrolled, selectedRowCount, selectedRows, scrollToTop, noVisibleRows } = this.state;

        const customGridOptions = this.buildCustomGridOptions();
        const totalVisibleCount = noVisibleRows ? 0 : totalItemCount;
        const tableCount = selectedRowCount === 0 ? totalVisibleCount : selectedRowCount;
        const domainHierarchy = selectedDomainFilter === DOMAIN_HIERARCHY_PARENT ? 'parent-domains' : 'subdomains';
        const dataAutomationId = `domain-mgt-${type}-${domainHierarchy}-table`;

        return (
            <div className={classes.tableWrapper}>
                <DomainManagementTableActionsRow
                    count={tableCount}
                    handleScrollToTop={this.setScrollToTop}
                    hasScrolled={hasScrolled}
                    orgDomainSelectData={orgDomainSelectData}
                    handleOrgDomainMultiSelectFilter={this.handleOrgDomainMultiSelectFilter}
                    selectedDomainFilter={selectedDomainFilter}
                    selectedRows={selectedRows}
                    type={type}
                />
                <Divider className={classes.noMargin} />
                <AgGridTableContainer
                    columnDefs={columnDefs}
                    context={{ domainGroups: domainGroups }} //For context you should match the name of the field used for reusability
                    customGridOptions={customGridOptions}
                    dataAutomationId={dataAutomationId}
                    isLoading={isLoading}
                    rowClickHandler={this.handleRowClick}
                    rowData={data}
                    scrollToTop={scrollToTop}
                    tableType={`${TABLE_TYPES.SCROLLING} ${activeClass( isActiveTable )}`}
                />
            </div>
        );
    }
};

DomainManagementTable.defaultProps = {
    isActiveTable: false,
    onRowClick: () => {}
};

DomainManagementTable.propTypes = {
    classes: PropTypes.object,
    columnDefs: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    domainGroups: PropTypes.array,
    handleFilterChange: PropTypes.func.isRequired,
    handleOrgDomainMultiSelectFilter: PropTypes.func,
    handlePaginationChange: PropTypes.func.isRequired,
    handleSortChange: PropTypes.func.isRequired,
    handleViewButtonAction: PropTypes.func,
    isActiveTable: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    noVisibleRows: PropTypes.bool,
    onRowClick: PropTypes.func.isRequired,
    orgDomainSelectData: PropTypes.array,
    selectedDomainFilter: PropTypes.string,
    totalItemCount: PropTypes.number,
    type: PropTypes.oneOf( ['claimed', 'discovered'] ).isRequired,
};

export default withStyles( styles )( DomainManagementTable );
